import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, CheckCircle2, MessageCircleQuestion, Sparkles, Shield, Zap } from 'lucide-react';
import { ARTICLES } from '../data/helpContent';
import { HelpSection } from '../components/help/HelpSection';
import { HelpStep } from '../components/help/HelpStep';
import { HelpCallout } from '../components/help/HelpCallout';
import { HelpTips } from '../components/help/HelpTips';
import { HelpTroubleshooting } from '../components/help/HelpTroubleshooting';
import { HelpFAQ } from '../components/help/HelpFAQ';
import { HelpTableOfContents } from '../components/help/HelpTableOfContents';
import { HelpDemo } from '../components/help/HelpDemo';

export default function HelpArticle() {
  const { slug } = useParams();
  const article = ARTICLES[slug];

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-400 text-lg mb-4">Article not found</p>
          <Link to="/help" className="text-orange-500 hover:text-orange-600 font-medium text-sm">
            ← Back to Help Center
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top nav */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-12 flex items-center gap-3">
          <Link
            to="/help"
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft size={14} />
            Help Center
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-sm text-slate-700 font-medium">{article.title}</span>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-start">

          {/* ── Main content ── */}
          <main className="flex-1 min-w-0 w-full">

            {/* Article header */}
            <div className="mb-6 sm:mb-10">
              <span className="inline-block text-xs font-semibold text-orange-600 bg-orange-50 border border-orange-100 px-2.5 py-1 rounded-full mb-3 uppercase tracking-wide">
                {article.category}
              </span>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 mb-3 break-words" style={{ fontFamily: 'Manrope' }}>
                {article.title}
              </h1>
              <p className="text-slate-500 text-sm sm:text-base leading-relaxed">
                {article.description}
              </p>
            </div>

            {/* Onboarding hint */}
            {article.onboarding && (
              <HelpCallout type="tip" title="First time here?">
                {article.onboarding}
              </HelpCallout>
            )}

            {/* Overview */}
            <HelpSection id="overview" title="Overview">
              <p className="text-slate-600 text-sm leading-relaxed">{article.overview}</p>
              {article.demo && (
                <div className="mt-4">
                  <HelpDemo type={article.demo} />
                </div>
              )}
              {article.screenshot && (
                <div className="mt-5 rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                  <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-100 flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-300" />
                      <div className="w-2 h-2 rounded-full bg-yellow-300" />
                      <div className="w-2 h-2 rounded-full bg-green-300" />
                    </div>
                    <span className="text-xs text-slate-400 ml-1">ReStaff App</span>
                  </div>
                  <img 
                    src={article.screenshot} 
                    alt={`${article.title} interface`} 
                    className="w-full block" 
                    loading="lazy"
                    onError={(e) => {
                      if (!e.target.src.includes('restaff.b-cdn.net')) {
                        e.target.src = 'https://restaff.b-cdn.net/Screenshots/sendpulse.png';
                      }
                    }}
                  />
                </div>
              )}
            </HelpSection>

            {/* What Eva Can Do — feature groups (eva-ai only) */}
            {article.evaFeatures && (
              <HelpSection id="features" title="What Eva Can Do">
                <div className="grid sm:grid-cols-2 gap-4">
                  {article.evaFeatures.map((group, gi) => (
                    <div
                      key={gi}
                      className="bg-white border border-slate-100 rounded-xl p-4 hover:border-orange-200 hover:shadow-sm transition-all"
                    >
                      <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                        <Zap size={13} className="text-orange-500 flex-shrink-0" />
                        {group.title}
                      </h4>
                      <ul className="space-y-1.5">
                        {group.items.map((item, ii) => (
                          <li key={ii} className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
                            <CheckCircle2 size={12} className="text-orange-400 flex-shrink-0 mt-0.5" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </HelpSection>
            )}

            {/* Benefits */}
            <HelpSection id="benefits" title="Key Benefits">
              <ul className="grid sm:grid-cols-2 gap-2">
                {article.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 bg-white border border-slate-100 rounded-xl p-3.5">
                    <CheckCircle2 size={15} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{b}</span>
                  </li>
                ))}
              </ul>
            </HelpSection>

            {/* Capabilities — scope-grouped feature list (MCP article) */}
            {article.capabilities && (
              <HelpSection id="capabilities" title="What you can do">
                {article.capabilities.intro && (
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    {article.capabilities.intro}
                  </p>
                )}
                <div className="grid sm:grid-cols-2 gap-4" data-testid="mcp-capabilities-grid">
                  {article.capabilities.groups.map((g, gi) => (
                    <div
                      key={gi}
                      className="bg-white border border-slate-100 rounded-xl p-4 hover:border-orange-200 hover:shadow-sm transition-all"
                      data-testid={`mcp-capability-${g.scope}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Zap size={13} className="text-orange-500 flex-shrink-0" />
                        <h4 className="text-sm font-bold text-slate-800">{g.title}</h4>
                      </div>
                      <code className="inline-block text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded mb-2.5">
                        {g.scope}
                      </code>
                      <ul className="space-y-1.5">
                        {g.items.map((it, ii) => (
                          <li key={ii} className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
                            <CheckCircle2 size={12} className="text-orange-400 flex-shrink-0 mt-0.5" />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </HelpSection>
            )}

            {/* Security — guarantee rows (MCP article) */}
            {article.security && (
              <HelpSection id="security" title="Security & Privacy">
                {article.security.intro && (
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    {article.security.intro}
                  </p>
                )}
                <div className="space-y-2" data-testid="mcp-security-rows">
                  {article.security.rows.map((r, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl p-3.5"
                    >
                      <Shield size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-800">{r.name}</div>
                        <div className="text-xs text-slate-600 leading-relaxed mt-0.5">{r.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </HelpSection>
            )}

            {/* What You Can Ask Eva — only for eva-ai article */}
            {article.askEvaQuestions && (
              <HelpSection id="ask-eva" title="What You Can Ask Eva">
                {/* Desktop: image right + text left / Mobile: image on top */}
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 mb-6">
                  {/* Left: subtitle + image (mobile) */}
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-500 text-sm leading-relaxed mb-5">
                      {article.askEvaQuestions.subtitle}
                    </p>

                    {/* Image — mobile only (above content) */}
                    <div className="block lg:hidden mb-5 w-full flex justify-center">
                      <img
                        src={article.askEvaQuestions.image}
                        alt="Eva AI Assistant"
                        className="w-full h-auto max-w-full rounded-xl shadow-md object-contain"
                        data-testid="ask-eva-image-mobile"
                      />
                    </div>

                    {/* Question groups */}
                    <div className="grid sm:grid-cols-2 gap-4" data-testid="ask-eva-question-groups">
                      {article.askEvaQuestions.groups.map((group, gi) => (
                        <div
                          key={gi}
                          className="bg-white border border-slate-100 rounded-xl p-4 hover:border-orange-200 hover:shadow-sm transition-all"
                          data-testid={`ask-eva-group-${gi}`}
                        >
                          <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                            <span className="text-base">{group.icon}</span>
                            {group.title}
                          </h4>
                          <ul className="space-y-2">
                            {group.questions.map((q, qi) => (
                              <li key={qi} className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
                                <MessageCircleQuestion size={12} className="text-orange-400 flex-shrink-0 mt-0.5" />
                                <span>{q}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    {/* Micro copy */}
                    <p className="mt-5 text-xs text-slate-400 italic leading-relaxed" data-testid="ask-eva-micro-copy">
                      {article.askEvaQuestions.microCopy}
                    </p>
                  </div>

                  {/* Right: image — desktop only */}
                  <div className="hidden lg:block w-72 flex-shrink-0">
                    <img
                      src={article.askEvaQuestions.image}
                      alt="Eva AI Assistant"
                      className="w-full rounded-xl shadow-md object-cover sticky top-24"
                      data-testid="ask-eva-image-desktop"
                    />
                  </div>
                </div>
              </HelpSection>
            )}

            {/* When to use */}
            {article.whenToUse && (
              <HelpSection id="when-to-use" title="When to Use This Feature">
                <HelpCallout type="info">
                  {article.whenToUse}
                </HelpCallout>
              </HelpSection>
            )}

            {/* Setup (optional) */}
            {article.setup && (
              <HelpSection id="setup" title="Initial Setup">
                <p className="text-slate-600 text-sm leading-relaxed">{article.setup}</p>
              </HelpSection>
            )}

            {/* Step-by-step guide */}
            {article.steps && article.steps.length > 0 && (
              <HelpSection id="guide" title="Step-by-Step Guide">
                <div className="mt-2">
                  {article.steps.map((step, i) => (
                    <React.Fragment key={i}>
                      <HelpStep number={i + 1} title={step.title}>
                        <p>{step.desc}</p>
                        {step.tip && (
                          <HelpCallout type="tip">
                            {step.tip}
                          </HelpCallout>
                        )}
                        {step.screenshot && (
                          <div className="mt-4 rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                            <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-100 flex items-center gap-2">
                              <div className="flex gap-1">
                                <div className="w-2 h-2 rounded-full bg-red-300" />
                                <div className="w-2 h-2 rounded-full bg-yellow-300" />
                                <div className="w-2 h-2 rounded-full bg-green-300" />
                              </div>
                              <span className="text-xs text-slate-400 ml-1">ReStaff App</span>
                            </div>
                            <img src={step.screenshot} alt={step.title} className="w-full block" loading="lazy" />
                          </div>
                        )}
                      </HelpStep>
                      {i < article.steps.length - 1 && (
                        <div className="ml-4 w-0.5 h-4 bg-slate-200 mb-2" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </HelpSection>
            )}

            {/* Eva in Action — screenshot grid (eva-ai only) */}
            {article.evaScreenshots && (
              <HelpSection id="eva-in-action" title="Eva in Action">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                  {article.evaScreenshots.map((ss, i) => (
                    <div key={i} className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                      <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-100 flex items-center gap-2">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-300" />
                          <div className="w-2 h-2 rounded-full bg-yellow-300" />
                          <div className="w-2 h-2 rounded-full bg-green-300" />
                        </div>
                        <span className="text-xs text-slate-400 ml-1">{ss.alt}</span>
                      </div>
                      <img
                        src={ss.src}
                        alt={ss.alt}
                        className="w-full block"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </HelpSection>
            )}

            {/* Scoring (optional — KPI / bonus articles) */}
            {article.scoring && (
              <HelpSection id="scoring" title="How the Score Works">
                {article.scoring.intro && (
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">{article.scoring.intro}</p>
                )}
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm" data-testid="kpi-scoring-table">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Metric</th>
                        <th className="text-left px-4 py-2 font-semibold w-24">Weight</th>
                        <th className="text-left px-4 py-2 font-semibold">Full points when…</th>
                      </tr>
                    </thead>
                    <tbody>
                      {article.scoring.rows.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-4 py-2.5 font-medium text-slate-800">{r.metric}</td>
                          <td className="px-4 py-2.5 text-slate-700">{r.weight}</td>
                          <td className="px-4 py-2.5 text-slate-600">{r.rule}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {article.scoring.thresholds && (
                  <div className="mt-5 space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Status thresholds</p>
                    {article.scoring.thresholds.map((t, i) => (
                      <div key={i} className="flex items-start gap-3 text-sm">
                        <span className={`mt-1 inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                          t.dot === 'green'  ? 'bg-emerald-500' :
                          t.dot === 'yellow' ? 'bg-amber-500'   :
                          'bg-red-500'
                        }`} />
                        <div>
                          <span className="font-semibold text-slate-800 font-mono text-xs mr-2">{t.label}</span>
                          <span className="text-slate-600">{t.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {article.scoring.footer && (
                  <p className="mt-4 text-xs text-slate-400 italic">{article.scoring.footer}</p>
                )}
              </HelpSection>
            )}

            {/* Prompt Examples (MCP article) */}
            {article.promptExamples && article.promptExamples.length > 0 && (
              <HelpSection id="prompts" title="Example Prompts">
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  Try any of these in ChatGPT or Claude after you have connected ReStaff. Copy-paste freely — the AI will pick the right MCP tool automatically.
                </p>
                <div className="grid sm:grid-cols-2 gap-4" data-testid="mcp-prompt-examples">
                  {article.promptExamples.map((pg, gi) => (
                    <div
                      key={gi}
                      className="bg-white border border-slate-100 rounded-xl p-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={13} className="text-orange-500 flex-shrink-0" />
                        <h4 className="text-sm font-bold text-slate-800">{pg.group}</h4>
                      </div>
                      <ul className="space-y-1.5">
                        {pg.prompts.map((p, pi) => (
                          <li
                            key={pi}
                            className="text-xs text-slate-700 leading-relaxed bg-slate-50 border border-slate-100 rounded-md px-2.5 py-1.5"
                          >
                            “{p}”
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </HelpSection>
            )}

            {/* Tips */}
            {article.tips && article.tips.length > 0 && (
              <HelpSection id="tips" title="Tips & Best Practices">
                <HelpTips items={article.tips} />
              </HelpSection>
            )}

            {/* Troubleshooting */}
            {article.troubleshooting && article.troubleshooting.length > 0 && (
              <HelpSection id="troubleshooting" title="Troubleshooting">
                <HelpTroubleshooting items={article.troubleshooting} />
              </HelpSection>
            )}

            {/* FAQ */}
            {article.faq && article.faq.length > 0 && (
              <HelpSection id="faq" title="Frequently Asked Questions">
                <HelpFAQ items={article.faq} />
              </HelpSection>
            )}

            {/* Footer nav */}
            <div className="mt-12 pt-6 border-t border-slate-200 flex items-center justify-between">
              <Link
                to="/help"
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft size={14} />
                Back to Help Center
              </Link>
              <div className="flex items-center gap-1.5 text-xs text-slate-400">
                <BookOpen size={13} />
                ReStaff Documentation
              </div>
            </div>

          </main>

          {/* ── Sticky Table of Contents ── */}
          <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-20 self-start">
            <HelpTableOfContents sections={article.sections} />
          </aside>

        </div>
      </div>
    </div>
  );
}
